import { cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react';
import { EmpreenderApps } from '../../lib/AppsArray';
import type { AppCardProps, AppDetails } from './Types';

import NuvemshopIcon from '../../ui/platformIcons/Nuvemshop';
import ShopifyIcon from '../../ui/platformIcons/Shopify';
import YampiIcon from '../../ui/platformIcons/Yampi';
import CartpandaIcon from '../../ui/platformIcons/Cartpanda';
import TinyIcon from '../../ui/platformIcons/Tiny';
import IsetIcon from '../../ui/platformIcons/Iset';
import LojaIntegradaIcon from '../../ui/platformIcons/LojaIntegrada';
import TrayIcon from '../../ui/platformIcons/Tray';
import CorreiosIcon from '../../ui/platformIcons/Correios';
import AdminerIcon from '../../ui/platformIcons/Adminer';
import BagyIcon from '../../ui/platformIcons/Bagy';
import WoocommerceIcon from '../../ui/platformIcons/Woocommerce';
import DocaCommerceIcon from '../../ui/platformIcons/DocaCommerce';
import FastcommerceIcon from '../../ui/platformIcons/Fastcommerce';
import WellcommerceIcon from '../../ui/platformIcons/Wellcommerce';
import LpqvIcon from '../../ui/platformIcons/Lpqv';
import MercadoLivreIcon from '../../ui/platformIcons/MercadoLivre';
import WixIcon from '../../ui/platformIcons/Wix';
import ShopeeIcon from '../../ui/platformIcons/Shopee';
import AliexpressIcon from '../../ui/platformIcons/Aliexpress';
import CjdropshippingIcon from '../../ui/platformIcons/Cjdropshipping';
import Icon3Cliques from '../../ui/platformIcons/3Cliques';
import SourcinboxIcon from '../../ui/platformIcons/Sourcinbox';
import HotmartIcon from '../../ui/platformIcons/Hotmart';
import MelhorenvioIcon from '../../ui/platformIcons/Melhorenvio';
import TiktokIcon from '../../ui/platformIcons/Tiktok';
import FrenetIcon from '../../ui/platformIcons/Frenet';
import EnviacomIcon from '../../ui/platformIcons/Enviacom';
import BlingIcon from '../../ui/platformIcons/Bling';
import OlistIcon from '../../ui/platformIcons/Olist';
import OpenaiIcon from '../../ui/platformIcons/Openai';
import MercadoPagoIcon from '../../ui/platformIcons/MercadoPago';
import WbuyIcon from '../../ui/platformIcons/Wbuy';

export const DEFAULT_TRIAL_HREF = 'https://app.empreender.plus/';
export const DEFAULT_UPGRADE_HREF = 'https://app.empreender.plus/planos';

/** `https://app.empreender.plus/planos?app={eplus}` */
export function upgradeHrefFor(eplus: string) {
	return `${DEFAULT_UPGRADE_HREF}?app=${eplus}`;
}

export type IntegracaoPreset = {
	title: string;
	description: string;
	icon: ReactElement;
	color: string;
	appEmpreender?: boolean;
	/** Código do app no Plus (ex.: DROPI). Usado na URL de upgrade. */
	eplus?: string;
	aliases?: string[];
	details?: Partial<AppDetails>;
};

/** Cores de fundo dos ícones (apps Empreender + plataformas). */
const APP_COLORS: Record<string, string> = {
	// Empreender apps
	fretehero: '#fedef4',
	cheguei: '#fbfbca',
	sak: '#ccf5e1',
	suporte: '#FCD4DA',
	automagico: '#FED0BC',
	botaozap: '#D3FEDC',
	campopersonalizado: '#d7fbd7',
	lilyreview: '#FBF3B0',
	lilyreviews: '#FBF3B0',
	dashanalytics: '#e6deef',
	appdashboard: '#F1EBEB',
	plus: '#E6DDED',
	empreenderplus: '#E6DDED',
	dropdigital: '#fee9fc',
	descricaopro: '#FDE8FB',
	dropi: '#C9E7FE',
	dijital: '#dff2fb',
	areadocliente: '#DCF4F9',
	area: '#ffe7d2',
	ovniinfluenciadores: '#defaed',
	suporteovni: '#FDDEF4',
	landingpage: '#EBEBEB',
	whatsly: '#E4FAF3',
	supercheckout: '#fdd4da',
	replayvideocommerce: '#FBEBD6',
	replay: '#FBEBD6',
	rastreio: '#fff1c5',
	rastreionet: '#fff1c5',
	superfavoritos: '#FED8E9',
	selly: '#ffd8e9',
	'365artes': '#EEF2FF',
	parcelamento: '#FCE7F3',
	promocaoagendada: '#E0E7FF',
	montink: '#DBEAFE',
	widgetinstagram: '#E9D5FF',
	widget: '#E9D5FF',
	widgettiktok: '#FED7AA',
	nuvemshop: '#DEEAFB',
	shopify: '#E6FBC4',
	yampi: '#FDD6B9',
	cartpanda: '#D7E1FF',
	tiny: '#E4EDFF',
	iset: '#EAEFFF',
	lojaintegrada: '#D6F6F6',
	tray: '#D2EEFF',
	correios: '#FFF3C4',
	adminer: '#FFE5E8',
	bagy: '#FFE4EF',
	woocommerce: '#ECDEFB',
	woo: '#ECDEFB',
	docacommerce: '#FFF3CE',
	dooca: '#FFF3CE',
	fastcommerce: '#E0EDF9',
	wellcommerce: '#FFEBE5',
	lpqv: '#DAF8F0',
	mercadolivre: '#FAF5CA',
	wix: '#EDEDED',
	shopee: '#FDE0DC',
	aliexpress: '#FBEFDC',
	cjdropshipping: '#FFE7D7',
	'3cliques': '#FAE2E7',
	sourcinbox: '#FDEDE5',
	hotmart: '#FFEAE4',
	melhorenvio: '#FFF3DA',
	tiktok: '#DFFBFB',
	frenet: '#C0C3EB',
	enviacom: '#FFF3C4',
	bling: '#E1FBF2',
	olist: '#DEE7FA',
	openai: '#E0F9F3',
	mercadopago: '#DAF4FF',
	wbuy: '#FBE8E3',
};

function normalizeKey(value: string): string {
	return value
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '');
}

function variantKeys(value: string): string[] {
	const key = normalizeKey(value);
	if (!key) return [];
	const keys = new Set<string>([key]);
	if (key.endsWith('s') && key.length > 3) keys.add(key.slice(0, -1));
	else keys.add(`${key}s`);
	if (key.endsWith('net') && key.length > 5) keys.add(key.slice(0, -3));
	return [...keys];
}

function asCardIcon(node: ReactNode): ReactElement | null {
	if (!isValidElement(node)) return null;
	const el = node as ReactElement<{ width?: number; height?: number; className?: string }>;
	return cloneElement(el, {
		width: 28,
		height: 28,
		className: el.props.className ? `${el.props.className} size-7` : 'size-7',
	});
}

function resolveColor(...candidates: Array<string | undefined>): string {
	for (const candidate of candidates) {
		if (!candidate) continue;
		const color = APP_COLORS[normalizeKey(candidate)];
		if (color) return color;
	}
	return '#F3F4F6';
}

function platformPreset(
	title: string,
	description: string,
	icon: ReactElement,
	opts?: { aliases?: string[] },
): IntegracaoPreset {
	return {
		title,
		description,
		icon: asCardIcon(icon) ?? icon,
		color: resolveColor(title, ...(opts?.aliases ?? [])),
		aliases: opts?.aliases,
	};
}

const PLATFORM_PRESETS: IntegracaoPreset[] = [
	platformPreset('Nuvemshop', 'Conecte sua loja Nuvemshop para gerenciar integrações e aplicativos.', <NuvemshopIcon />, {
		aliases: ['nuvem shop'],
	}),
	platformPreset('Shopify', 'Conecte sua loja Shopify para sincronizar produtos, pedidos e estoque.', <ShopifyIcon />),
	platformPreset('Yampi', 'Integre com a Yampi para checkout e operação de vendas.', <YampiIcon />),
	platformPreset('Cartpanda', 'Conecte a Cartpanda para vender e gerenciar seus pedidos.', <CartpandaIcon />),
	platformPreset('Tiny', 'Integre com o Tiny ERP para gestão de estoque, notas e pedidos.', <TinyIcon />, {
		aliases: ['tiny erp', 'tinyerp'],
	}),
	platformPreset('iSET', 'Integre sua operação com a plataforma iSET.', <IsetIcon />, {
		aliases: ['iset'],
	}),
	platformPreset('Loja Integrada', 'Conecte sua Loja Integrada aos aplicativos Empreender.', <LojaIntegradaIcon />, {
		aliases: ['lojaintegrada'],
	}),
	platformPreset('Tray', 'Integre com a Tray Commerce para gerenciar sua loja.', <TrayIcon />),
	platformPreset('Correios', 'Acompanhe envios e rastreio com os Correios.', <CorreiosIcon />),
	platformPreset('Adminer', 'Conecte a Adminer à sua operação de e-commerce.', <AdminerIcon />),
	platformPreset('Bagy', 'Integre com a Bagy para vender na sua plataforma.', <BagyIcon />),
	platformPreset('WooCommerce', 'Conecte sua loja WooCommerce aos apps Empreender.', <WoocommerceIcon />, {
		aliases: ['woo', 'woocommerce'],
	}),
	platformPreset('Dooca Commerce', 'Integre com a Dooca Commerce.', <DocaCommerceIcon />, {
		aliases: ['dooca', 'docacommerce'],
	}),
	platformPreset('Fastcommerce', 'Conecte sua operação Fastcommerce.', <FastcommerceIcon />),
	platformPreset('Well Commerce', 'Integre com a Well Commerce.', <WellcommerceIcon />, {
		aliases: ['wellcommerce', 'well.commerce'],
	}),
	platformPreset('LPQV', 'Conecte a LPQV à sua loja.', <LpqvIcon />),
	platformPreset('Mercado Livre', 'Sincronize produtos e pedidos com o Mercado Livre.', <MercadoLivreIcon />, {
		aliases: ['mercadolivre', 'mercado livre'],
	}),
	platformPreset('Wix', 'Conecte sua loja Wix.', <WixIcon />),
	platformPreset('Shopee', 'Integre com a Shopee para ampliar suas vendas.', <ShopeeIcon />),
	platformPreset('AliExpress', 'Importe e sincronize produtos do AliExpress.', <AliexpressIcon />, {
		aliases: ['aliexpress'],
	}),
	platformPreset('CJ Dropshipping', 'Conecte o CJ Dropshipping à sua operação.', <CjdropshippingIcon />, {
		aliases: ['cj', 'cjdropshipping'],
	}),
	platformPreset('3 Cliques', 'Integre com a 3 Cliques.', <Icon3Cliques />, {
		aliases: ['3cliques'],
	}),
	platformPreset('Sourcinbox', 'Conecte a Sourcinbox para sourcing e dropshipping.', <SourcinboxIcon />),
	platformPreset('Hotmart', 'Integre com a Hotmart para infoprodutos.', <HotmartIcon />),
	platformPreset('Melhor Envio', 'Calcule e gerencie fretes com o Melhor Envio.', <MelhorenvioIcon />, {
		aliases: ['melhorenvio'],
	}),
	platformPreset('TikTok', 'Conecte o TikTok Shop à sua loja.', <TiktokIcon />, {
		aliases: ['tiktok shop', 'tiktokshop'],
	}),
	platformPreset('Frenet', 'Calcule fretes com a Frenet.', <FrenetIcon />),
	platformPreset('Envia.com', 'Gerencie envios com a Envia.com.', <EnviacomIcon />, {
		aliases: ['enviacom', 'envia'],
	}),
	platformPreset('Bling', 'Integre ERP e pedidos com o Bling.', <BlingIcon />),
	platformPreset('Olist', 'Conecte sua operação à Olist.', <OlistIcon />),
	platformPreset('OpenAI', 'Use recursos de IA com a OpenAI.', <OpenaiIcon />, {
		aliases: ['openai', 'chatgpt'],
	}),
	platformPreset('Mercado Pago', 'Receba pagamentos com o Mercado Pago.', <MercadoPagoIcon />, {
		aliases: ['mercadopago'],
	}),
	platformPreset('Wbuy', 'Integre com a Wbuy.', <WbuyIcon />),
];

function buildPresets(): IntegracaoPreset[] {
	const fromApps: IntegracaoPreset[] = [];

	for (const app of EmpreenderApps) {
		if (app.hide) continue;
		const icon = asCardIcon(app.icon);
		if (!icon) continue;

		const rawContent = typeof app.content === 'string' ? app.content.trim() : '';
		const description = rawContent || '';

		fromApps.push({
			title: app.name,
			description,
			icon,
			color: resolveColor(app.name, app.slug, app.eplus),
			appEmpreender: true,
			eplus: app.eplus,
			// Não usa tags de busca do AppsArray (ex.: "tiktok") — colidem com plataformas.
			aliases: [app.slug, app.eplus].filter(Boolean),
			details: {
				openAppHref: `${DEFAULT_TRIAL_HREF}?app=${app.eplus}`,
				plusHref: DEFAULT_TRIAL_HREF,
			},
		});
	}

	return [...fromApps, ...PLATFORM_PRESETS];
}

export const integracaoPresets: IntegracaoPreset[] = buildPresets();

/** Mapa title → descrição (sidebar / plataformas), útil para docs e overrides. */
export const integracaoDescriptions: Record<string, string> = Object.fromEntries(
	integracaoPresets.map((preset) => [preset.title, preset.description]),
);

const presetIndex: Map<string, IntegracaoPreset> = (() => {
	const index = new Map<string, IntegracaoPreset>();

	const register = (preset: IntegracaoPreset) => {
		// Título sempre tem prioridade sobre aliases genéricos
		for (const key of variantKeys(preset.title)) {
			index.set(key, preset);
		}
		for (const alias of preset.aliases ?? []) {
			for (const key of variantKeys(alias)) {
				if (!index.has(key)) index.set(key, preset);
			}
		}
	};

	for (const preset of integracaoPresets) register(preset);
	return index;
})();

export function findIntegracaoPreset(title: string): IntegracaoPreset | undefined {
	const exact = normalizeKey(title);
	const byExactTitle = integracaoPresets.find((preset) => normalizeKey(preset.title) === exact);
	if (byExactTitle) return byExactTitle;

	for (const key of variantKeys(title)) {
		const preset = presetIndex.get(key);
		if (preset) return preset;
	}
	return undefined;
}

/**
 * Mescla o card do consumidor com o preset pelo `title`.
 * - description / icon / color / appEmpreender vêm do preset se não forem passados
 * - details.openAppHref e details.plusHref só para apps Empreender (podem ser sobrescritos)
 * - apps Empreender não instalados recebem `trialHref` automático
 * - plataformas externas só exibem "Teste grátis" se `trialHref` for passado
 * - instalados não exibem "Teste grátis"
 * - `upgrade: true` monta `https://app.empreender.plus/planos?app={eplus}`
 */
export function resolveIntegracaoCard(card: AppCardProps): AppCardProps {
	const preset = findIntegracaoPreset(card.title);
	const appEmpreender = card.appEmpreender ?? preset?.appEmpreender ?? false;
	const installed = card.installed ?? false;

	const trialHref = installed
		? undefined
		: card.trialHref !== undefined
			? card.trialHref || undefined
			: appEmpreender
				? DEFAULT_TRIAL_HREF
				: undefined;

	const upgradeHref =
		card.upgradeHref ||
		(card.upgrade && preset?.eplus ? upgradeHrefFor(preset.eplus) : undefined);

	const details = resolveDetails(preset?.details, card.details, appEmpreender);

	return {
		...card,
		title: card.title || preset?.title || '',
		description: card.description ?? preset?.description ?? '',
		icon: card.icon ?? preset?.icon ?? <span className='size-7' />,
		color: card.color ?? preset?.color,
		appEmpreender,
		tags: card.tags ?? [],
		trialHref,
		upgradeHref,
		details,
	};
}

function resolveDetails(
	presetDetails: Partial<AppDetails> | undefined,
	cardDetails: AppDetails | undefined,
	appEmpreender: boolean,
): AppDetails | undefined {
	const merged: AppDetails = {
		...(appEmpreender ? presetDetails : undefined),
		...cardDetails,
	};

	if (appEmpreender) {
		if (!merged.openAppHref && presetDetails?.openAppHref) {
			merged.openAppHref = presetDetails.openAppHref;
		}
		if (!merged.plusHref) {
			merged.plusHref = presetDetails?.plusHref ?? DEFAULT_TRIAL_HREF;
		}
	} else {
		// Plataformas externas: Abrir App / Empreender Plus só existem em apps Empreender
		delete merged.openAppHref;
		delete merged.plusHref;
	}

	const hasAny = !!(merged.chave || merged.storeUrl || merged.openAppHref || merged.plusHref);
	return hasAny ? merged : undefined;
}

export function resolveIntegracaoCards(cards: AppCardProps[]): AppCardProps[] {
	return cards.map(resolveIntegracaoCard);
}
